"use client";

import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { ChangeEvent, FormEvent } from "react";
import { createPortal } from "react-dom";
import dynamic from "next/dynamic";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { FiRefreshCw } from "react-icons/fi";
import { Link } from "@/i18n/routing";
import type { Application } from "@splinetool/runtime";

// Spline is heavy: load its code only when it is actually needed
const Spline = dynamic(() => import("@splinetool/react-spline"), {
  ssr: false,
});

if (typeof window !== "undefined") {
  gsap.registerPlugin(CustomEase);
  CustomEase.create("hop", ".87,0,.13,1");
}

const SPLINE_SCENE =
  "https://prod.spline.design/BNaurVSeS57NeyWI/scene.splinecode";
const splineStyle = { background: "transparent" } as const;

/* ------------------------------ helpers ------------------------------ */

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

let webglCache: boolean | null = null;
function hasWebGL(): boolean {
  if (webglCache !== null) return webglCache;
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    webglCache = !!gl;
    // release the throwaway context right away
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  } catch {
    webglCache = false;
  }
  return webglCache;
}

const canUseSpline = () =>
  window.matchMedia("(min-width: 1000px)").matches &&
  !prefersReducedMotion() &&
  hasWebGL();

/* ---------------------------- media column ---------------------------- */

const MediaColumn = memo(function MediaColumn({
  showSpline,
}: {
  showSpline: boolean;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!showSpline) setLoaded(false);
  }, [showSpline]);

  const onLoad = useCallback((spline: Application) => {
    spline.setBackgroundColor("transparent" as any);
    setLoaded(true);
  }, []);

  const onError = useCallback(() => setFailed(true), []);

  return (
    <div className="lt-media relative hidden flex-[2] bg-secondary min-[1000px]:block">
      {showSpline && !failed && (
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        >
          <Spline
            scene={SPLINE_SCENE}
            onLoad={onLoad}
            onError={onError}
            style={splineStyle}
          />
        </div>
      )}
      <h2 className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-center text-[clamp(3rem,7vw,8rem)] font-bold leading-none text-primary">
        Let&apos;s Talk
      </h2>
    </div>
  );
});

/* ----------------------------- contact form ----------------------------- */

interface FormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
}

interface CaptchaChallenge {
  question: string;
  answer: number;
}

const EMPTY_FORM: FormData = {
  name: "",
  company: "",
  email: "",
  phone: "",
  message: "",
};

const fieldClass =
  "w-full rounded-3xl border border-white bg-transparent px-4 py-3 text-base text-white outline-none placeholder:text-white focus:border-primary";

// Owns its own state, so typing never re-renders the overlay shell or Spline
const ContactForm = memo(function ContactForm() {
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [captcha, setCaptcha] = useState<CaptchaChallenge>({
    question: "",
    answer: 0,
  });
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaValid, setCaptchaValid] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{
    type: "success" | "error" | "";
    message: string;
  }>({ type: "", message: "" });

  const generateCaptcha = () => {
    const operations = [
      { op: "+", fn: (a: number, b: number) => a + b },
      { op: "-", fn: (a: number, b: number) => a - b },
      { op: "×", fn: (a: number, b: number) => a * b },
    ];
    const num1 = Math.floor(Math.random() * 20) + 1;
    const num2 = Math.floor(Math.random() * 20) + 1;
    const selectedOp = operations[Math.floor(Math.random() * operations.length)];

    setCaptcha({
      question: `${num1} ${selectedOp.op} ${num2} = ?`,
      answer: selectedOp.fn(num1, num2),
    });
    setCaptchaInput("");
    setCaptchaValid(false);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCaptchaChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCaptchaInput(value);
    setCaptchaValid(parseInt(value) === captcha.answer);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      setStatus({ type: "error", message: "Please fill in all required fields." });
      return;
    }

    if (!showCaptcha) {
      setShowCaptcha(true);
      generateCaptcha();
      setStatus({ type: "", message: "Please solve the CAPTCHA to continue." });
      return;
    }

    if (!captchaValid) {
      setStatus({ type: "error", message: "Incorrect CAPTCHA answer. Please try again." });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();

      if (response.ok) {
        setStatus({ type: "success", message: "Thanks! Your message has been sent." });
        setFormData(EMPTY_FORM);
        setShowCaptcha(false);
        setCaptchaInput("");
        setCaptchaValid(false);
      } else {
        setStatus({
          type: "error",
          message: data.error || "Something went wrong. Please try again.",
        });
      }
    } catch {
      setStatus({ type: "error", message: "Network error. Please try again later." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div className="lt-item grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          type="text"
          name="name"
          autoComplete="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Full name *"
          className={fieldClass}
        />
        <input
          type="text"
          name="company"
          autoComplete="organization"
          value={formData.company}
          onChange={handleInputChange}
          placeholder="Company"
          className={fieldClass}
        />
      </div>

      <div className="lt-item grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          type="email"
          name="email"
          autoComplete="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="Email *"
          className={fieldClass}
        />
        <input
          type="tel"
          name="phone"
          autoComplete="tel"
          value={formData.phone}
          onChange={handleInputChange}
          placeholder="Phone *"
          className={fieldClass}
        />
      </div>

      <div className="lt-item">
        <textarea
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          rows={5}
          placeholder="Project Details *"
          className={`${fieldClass} resize-none`}
        />
      </div>

      {showCaptcha && (
        <div className="flex flex-col items-center gap-3 py-2">
          <div className="flex w-60 items-center justify-around gap-2 rounded-3xl bg-white/90 px-4 py-2 shadow-sm">
            <p className="text-center font-mono text-xl font-bold tracking-wider text-secondary">
              {captcha.question}
            </p>
            <button
              type="button"
              onClick={generateCaptcha}
              aria-label="New CAPTCHA"
              className="text-secondary"
            >
              <FiRefreshCw size={20} />
            </button>
          </div>

          <input
            type="number"
            value={captchaInput}
            onChange={handleCaptchaChange}
            placeholder="Answer"
            className={`w-60 rounded-3xl border bg-secondary px-4 py-2 text-base text-white outline-none transition-colors ${
              captchaInput === ""
                ? "border-white/30"
                : captchaValid
                ? "border-green-400"
                : "border-red-400"
            }`}
          />
        </div>
      )}

      {status.message && (
        <p
          className={`text-center text-xs font-medium md:text-sm ${
            status.type === "success"
              ? "text-green-400"
              : status.type === "error"
              ? "text-red-400"
              : "text-white"
          }`}
        >
          {status.message}
        </p>
      )}

      <div className="lt-item">
        <button
          type="submit"
          disabled={loading || (showCaptcha && !captchaValid)}
          className="w-full rounded-3xl bg-white py-3 font-semibold text-secondary transition-colors hover:bg-accent hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Sending...
            </span>
          ) : showCaptcha && !captchaValid ? (
            "Solve CAPTCHA"
          ) : (
            "Submit"
          )}
        </button>
      </div>

      <p className="lt-item mt-4 text-xs text-white md:text-sm">
        By submitting this form, you agree to receive communications from Ignite
        Visibility, including marketing emails and SMS messages if applicable.
        You also agree to our{" "}
        <Link href="/privacy-policy" className="text-accent underline">
          Privacy Policy
        </Link>
        ,{" "}
        <Link href="/terms-and-conditions" className="text-accent underline">
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link href="/cookies-policy" className="text-accent underline">
          Cookies Policy
        </Link>
        .
      </p>
    </form>
  );
});

/* -------------------------------- main -------------------------------- */

export default function Letstalk() {
  // The overlay is only added to the DOM the first time it is opened
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showSpline, setShowSpline] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  const reducedRef = useRef(false);

  const play = useCallback(() => {
    const tl = tlRef.current;
    if (!tl || !overlayRef.current) return;
    gsap.set(overlayRef.current, { visibility: "visible" });
    tl.timeScale(reducedRef.current ? 50 : 1).play();
    closeBtnRef.current?.focus({ preventScroll: true });
  }, []);

  const openMenu = useCallback(() => {
    setIsOpen(true);
    if (!mounted) setMounted(true); // the layout effect below builds and plays the timeline
    else play();
  }, [mounted, play]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    tlRef.current?.timeScale(reducedRef.current ? 50 : 1.4).reverse();
    triggerRef.current?.focus({ preventScroll: true });
  }, []);

  // Build ONE timeline; closing is simply that timeline played in reverse.
  // Only transform and opacity are animated, so it runs on the compositor.
  useLayoutEffect(() => {
    if (!mounted) return;
    const overlay = overlayRef.current;
    const inner = innerRef.current;
    if (!overlay || !inner) return;

    reducedRef.current = prefersReducedMotion();

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        paused: true,
        onComplete: () => {
          // Mount the heavy WebGL scene only after the animation has finished
          if (canUseSpline()) setShowSpline(true);
        },
        onReverseComplete: () => {
          gsap.set(overlay, { visibility: "hidden" });
          setShowSpline(false); // frees the WebGL scene
        },
      });

      tl.fromTo(overlay, { yPercent: -100 }, { yPercent: 0, duration: 0.9, ease: "hop" }, 0)
        .fromTo(inner, { yPercent: 50 }, { yPercent: 0, duration: 0.9, ease: "hop" }, 0)
        .fromTo(".lt-media", { opacity: 0 }, { opacity: 1, duration: 0.6, ease: "power2.out" }, 0.4)
        .fromTo(
          ".lt-item",
          { y: 28, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.7, stagger: 0.06, ease: "power3.out" },
          0.3
        );

      tlRef.current = tl;
    }, overlay);

    play();

    return () => {
      ctx.revert();
      tlRef.current = null;
    };
  }, [mounted, play]);

  // Lock page scroll + close on Escape, only while open
  useEffect(() => {
    if (!isOpen) return;

    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      html.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, closeMenu]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openMenu}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
         className={`py-4 px-8 w-fit rounded-full text-white font-semibold text-base xl:text-lg bg-secondary hover:bg-accent transition-colors duration-300 `}
      >
     Request Growth Strategy
      </button>

      {mounted &&
        createPortal(
          <div
            ref={overlayRef}
            role="dialog"
            aria-modal="true"
            aria-label="Contact us"
            className="invisible fixed inset-0 z-[10000] overflow-hidden bg-secondary text-white"
          >
            <button
              ref={closeBtnRef}
              type="button"
              onClick={closeMenu}
              aria-label="Close menu"
              className="absolute right-4 top-4 z-20 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-white/10 bg-secondary hover:bg-primary sm:right-6 sm:top-6 lg:right-28 lg:top-12 lg:h-12 lg:w-12"
            >
              <span
                aria-hidden
                className="absolute h-[1.25px] w-[15px] rotate-45 scale-x-105 bg-white"
              />
              <span
                aria-hidden
                className="absolute h-[1.25px] w-[15px] -rotate-45 scale-x-105 bg-white"
              />
            </button>

            <div ref={innerRef} className="flex h-full w-full items-stretch">
              <MediaColumn showSpline={showSpline} />

              {/* Scrollable column. The inner wrapper is at least as tall as the
                  column and centers its content. On tablets (md) the bottom
                  padding is larger than the top, which lifts the form slightly
                  above the exact middle (optical center). On lg the padding is
                  equal again. Taller content scrolls from the top. */}
              <div
                data-lenis-prevent
                className="min-w-0 flex-[3] overflow-y-auto overscroll-contain"
              >
                <div className="flex min-h-full w-full items-center justify-center px-5 pb-24 pt-24 sm:px-10 md:px-16 md:pb-48 lg:px-12 lg:pb-16 lg:pt-16">
                  <div className="w-full max-w-2xl">
                    <h2 className="lt-item mb-6 text-3xl font-bold text-white sm:text-5xl lg:mb-10 lg:text-6xl xl:text-7xl">
                      Tap Into the Extraordinary
                    </h2>
                    <ContactForm />
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}